import React from 'react';
import { start, pollCaptcha2, verifyCaptcha2, pollResult, refreshCaptcha2 } from '../../services/ceacStatus';
import { LOCATIONS } from './locations';
import Footer from '../shared/Footer';

function withTimeout(promise, ms, message = '请求超时') {
  let timer;
  const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(message)), ms); });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

const Field = ({ label, children }) => (
  <label className="block text-sm text-gray-700 mb-3">
    <div className="font-medium mb-1">{label}</div>
    {children}
  </label>
);

export default function NivStatusCheck() {
  const [error, setError] = React.useState('');
  const [locations] = React.useState(LOCATIONS);
  const [form, setForm] = React.useState({ location: LOCATIONS[0]?.value || '', application_id: '', passport_number: '', surname_first5: '' });
  const [result, setResult] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [taskId, setTaskId] = React.useState('');
  const [captchaImg, setCaptchaImg] = React.useState('');
  const [captchaUrl, setCaptchaUrl] = React.useState('');
  const [captchaPhase, setCaptchaPhase] = React.useState('');
  const [captchaModal, setCaptchaModal] = React.useState(false);
  const [captchaValue, setCaptchaValue] = React.useState('');

  const handleSubmitStart = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(''); setResult(null);
    setCaptchaImg(''); setCaptchaValue('');
    try {
      const payload = {
        location: form.location,
        application_id: (form.application_id || '').trim(),
        passport_number: (form.passport_number || '').trim(),
        surname_first5: (form.surname_first5 || '').toUpperCase().slice(0,5),
        auto_solve: false,
      };
      const { task_id } = await withTimeout(start(payload), 10000, '创建任务超时');
      setTaskId(task_id);

      // 手动模式：显示验证码弹窗
      setCaptchaModal(true);
      const poll = async (attempt = 1) => {
        try {
          console.log(`📥 轮询验证码 [尝试 ${attempt}] - task_id=${task_id}`);
          const r = await withTimeout(pollCaptcha2(task_id), 15000, '验证码请求超时');
          console.log('📤 pollCaptcha2返回:', r);

          if (r && (r.captcha_image || r.captcha_url)) {
            console.log('✅ 收到验证码', {
              hasImage: !!r.captcha_image,
              hasUrl: !!r.captcha_url,
              phase: r.phase
            });
            if (r.captcha_image) {
              setCaptchaImg(r.captcha_image);
              console.log('✅ captchaImg已设置');
            }
            if (r.captcha_url) {
              setCaptchaUrl(r.captcha_url);
              console.log('✅ captchaUrl已设置');
            }
            if (r.phase) {
              setCaptchaPhase(r.phase);
              console.log(`📊 phase设置为: ${r.phase}`);
            }
            return;
          } else {
            console.log(`⏳ 验证码未就绪，继续轮询... [尝试 ${attempt}]`);
            if (attempt < 30) {
              setTimeout(() => poll(attempt + 1), 1500);
            } else {
              console.error('❌ 验证码获取超时，达到最大尝试次数');
              setError('验证码获取超时，请重新提交');
            }
          }
        } catch (err) {
          console.error('❌ 轮询验证码异常:', err);
          setError(err?.message || '获取验证码失败');
        }
      };
      poll();
    } catch (e) {
      setError(e?.message || '创建任务失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCaptcha = async () => {
    console.log('🔵 点击提交验证码', {taskId, captchaValue, captchaPhase});
    if (!taskId || !captchaValue) {
      console.warn('❌ 被拦截：taskId或captchaValue为空', {taskId, captchaValue});
      setError('请输入验证码');
      return;
    }
    setSubmitting(true); setError('');
    try {
      console.log('📤 调用verifyCaptcha2...');
      await withTimeout(verifyCaptcha2(taskId, captchaValue, captchaPhase), 10000, '提交验证码超时');
      console.log('✅ verifyCaptcha2成功');

      // ===== 关键修复：检查验证是否真的成功 =====
      // 给后端一点时间处理验证和刷新验证码
      await new Promise(resolve => setTimeout(resolve, 800));

      // 检查是否有新的验证码（说明验证失败了）
      try {
        const captchaCheck = await pollCaptcha2(taskId);
        if (captchaCheck && (captchaCheck.captcha_image || captchaCheck.captcha_url)) {
          // 检测到验证码返回，检查是否是新验证码
          console.log('📸 检查验证码:', {
            isNewImage: captchaCheck.captcha_image && captchaCheck.captcha_image !== captchaImg,
            isNewUrl: captchaCheck.captcha_url,
            phase: captchaCheck.phase
          });

          if (captchaCheck.captcha_image && captchaCheck.captcha_image !== captchaImg) {
            // 验证失败！验证码已更新
            console.log('⚠️ 验证失败：检测到新验证码，用户需要重新输入');
            setCaptchaImg(captchaCheck.captcha_image);
            setCaptchaUrl(captchaCheck.captcha_url || '');
            setCaptchaValue('');  // 清空输入框
            setCaptchaPhase(captchaCheck.phase || 'form');
            setError('验证码错误，请重新输入');
            setSubmitting(false);
            return;  // 不继续轮询结果
          }
        }
      } catch (err) {
        // 可能是 404 或其他错误，继续尝试轮询结果
        console.debug('验证码检查结果:', err?.message);
      }

      // 验证成功，开始轮询结果
      console.log('✅ 验证码验证成功，开始轮询结果...');
      const pollRes = async () => {
        try {
          const r = await withTimeout(pollResult(taskId), 8000, '结果请求超时');
          if (r) {
            console.log('✅ 收到结果:', r);
            setResult(r);
            setCaptchaModal(false);
          } else {
            setTimeout(pollRes, 1500);
          }
        } catch (err) {
          console.error('❌ 结果轮询失败:', err);
          setError(err?.message || '查询结果失败');
        }
      };
      pollRes();
    } catch (e) {
      console.error('❌ 提交验证码失败:', e);
      setError(e?.message || '提交验证码失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">签证状态查询（NIV）</h1>
        <p className="text-gray-600 mb-6">选择受理地点，填入 Application ID（如 AA00F4TDKH）。点击提交后获取验证码。</p>

        {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm">{error}</div>}

        <form onSubmit={handleSubmitStart} className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="受理地点（使领馆）">
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={form.location}
                onChange={e => setForm({ ...form, location: e.target.value })}
                required
              >
                {locations.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </Field>
            <Field label="Application ID（示例 AA00F4TDKH）">
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={form.application_id}
                onChange={e => setForm({ ...form, application_id: e.target.value })}
                required
                autoComplete="off"
              />
            </Field>
            <Field label="护照号 (Passport Number)">
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={form.passport_number}
                onChange={e => setForm({ ...form, passport_number: e.target.value })}
                required
                autoComplete="off"
              />
            </Field>
            <Field label="姓氏前5个字母 (First 5 Letters of Surname)">
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={form.surname_first5}
                onChange={e => setForm({ ...form, surname_first5: e.target.value.toUpperCase().slice(0,5) })}
                required
                autoComplete="off"
              />
            </Field>
          </div>

          <div className="mt-6 flex gap-3">
            <button type="submit" disabled={submitting} className="px-5 py-2 rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 disabled:opacity-60">
              {submitting ? '处理中...' : '提交查询'}
            </button>
            <button type="button" onClick={() => { setResult(null); setError(''); setCaptchaImg(''); setCaptchaValue(''); setTaskId(''); }} className="px-5 py-2 rounded-xl bg-white border">重置</button>
            <a
              href="https://usvisa.lvbaobao.space/"
              className="px-5 py-2 rounded-xl bg-white border"
            >
              返回首页
            </a>
          </div>
        </form>

        {result && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-5">
            <div className="font-semibold text-gray-900 mb-1">查询结果</div>
            <div className="text-gray-700">
              <span className="font-mono text-sm mr-1">{result.status_code}</span>
              {result.status_text || '（未能解析状态文本）'}
            </div>
            {result.detail && <div className="text-gray-600 text-sm mt-2">{result.detail}</div>}

            {/* 显示提交结果截图 */}
            {result.screenshot && (
              <div className="mt-4">
                <div className="text-sm text-gray-600 mb-2">提交结果截图（点击放大）：</div>
                <img
                  src={result.screenshot}
                  alt="提交结果截图"
                  className="w-full border rounded cursor-pointer hover:scale-105 transition-transform"
                  style={{maxHeight: '600px', objectFit: 'contain'}}
                  onClick={() => window.open(result.screenshot, '_blank')}
                />
              </div>
            )}
          </div>
        )}

        {captchaModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-5 w-full max-w-md border border-gray-200">
              <div className="font-semibold text-gray-900 mb-3">请输入验证码</div>
              {!(captchaImg || captchaUrl) ? (
                <div className="text-gray-600 text-sm flex items-center justify-between">
                  <span>正在获取验证码{captchaPhase === 'precheck' ? '（预检 1/2）' : (captchaPhase === 'form' ? '（提交 2/2）' : '')}...</span>
                  <button type="button" className="text-blue-600 hover:underline" onClick={async ()=>{
                    try {
                      console.log('🔄 用户点击刷新验证码');
                      setError('');
                      const r = await withTimeout(refreshCaptcha2(taskId), 15000, '验证码请求超时');
                      console.log('🔄 refreshCaptcha2返回:', r);
                      if (r) {
                        if (r.captcha_image) {
                          setCaptchaImg(r.captcha_image);
                          console.log('✅ 刷新后设置captchaImg');
                        }
                        if (r.captcha_url) {
                          setCaptchaUrl(r.captcha_url);
                          console.log('✅ 刷新后设置captchaUrl');
                        }
                      } else {
                        console.warn('⚠️ refreshCaptcha2返回null');
                      }
                    } catch(e){
                      console.error('❌ 刷新验证码失败:', e);
                      setError(e?.message||'获取验证码失败');
                    }
                  }}>刷新验证码</button>
                </div>
              ) : (
                <>
                  <img
                    src={captchaImg || captchaUrl}
                    alt="验证码"
                    className="w-full border rounded mb-3 cursor-pointer hover:scale-105 transition-transform"
                    style={{minHeight: '200px', maxHeight: '500px', objectFit: 'contain'}}
                    onClick={(e) => {
                      // 点击图片放大查看
                      window.open(captchaImg || captchaUrl, '_blank');
                    }}
                    onError={(e) => {
                      console.warn('⚠️ 验证码图片加载失败，尝试重新获取');
                      // 图片加载失败时，清空并继续轮询
                      if (!captchaImg && captchaUrl) {
                        console.log('URL加载失败，等待base64...');
                        // 可以在这里触发重新轮询
                      }
                    }}
                  />
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      type="button"
                      className="text-sm text-blue-600 hover:underline"
                      onClick={async ()=>{
                        try {
                          console.log('🔄 用户点击刷新验证码');
                          setError('');
                          setCaptchaImg('');
                          setCaptchaUrl('');
                          const r = await withTimeout(refreshCaptcha2(taskId), 15000, '验证码请求超时');
                          console.log('🔄 refreshCaptcha2返回:', r);
                          if (r) {
                            if (r.captcha_image) {
                              setCaptchaImg(r.captcha_image);
                              console.log('✅ 刷新后设置captchaImg');
                            }
                            if (r.captcha_url) {
                              setCaptchaUrl(r.captcha_url);
                              console.log('✅ 刷新后设置captchaUrl');
                            }
                          } else {
                            console.warn('⚠️ refreshCaptcha2返回null');
                          }
                        } catch(e){
                          console.error('❌ 刷新验证码失败:', e);
                          setError(e?.message||'获取验证码失败');
                        }
                      }}>
                      🔄 看不清？刷新验证码
                    </button>
                    <span className="text-xs text-gray-500">点击图片可放大查看</span>
                  </div>
                  <input className="w-full border rounded px-3 py-2 mb-3" value={captchaValue} onChange={e=>setCaptchaValue(e.target.value)} placeholder="输入图中的字符" />
                  <div className="flex gap-3 justify-end">
                    <button className="px-4 py-2 rounded bg-gray-100 border" type="button" onClick={()=>setCaptchaModal(false)}>取消</button>
                    <button className="px-4 py-2 rounded text-white bg-gradient-to-r from-blue-600 to-indigo-600" type="button" onClick={handleVerifyCaptcha} disabled={submitting}>{submitting? '提交中...' : '提交验证码'}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
      <Footer variant="simple" showCopyright={false} />
    </>
  );
}
